import { supabase } from '../lib/supabase';

/**
 * Bail Schedule Service
 * Handles all interactions with the bail_schedule table
 */

/**
 * Search bail schedule with filters
 * @param {Object} params - Search parameters
 * @param {string} params.searchTerm - Search term for offense description or code
 * @param {string} params.category - Category filter
 * @param {number} params.minBail - Minimum bail amount
 * @param {number} params.maxBail - Maximum bail amount
 * @returns {Promise<Object>} Search results
 */
export const searchBailSchedule = async ({
  searchTerm = null,
  category = null,
  minBail = null,
  maxBail = null
} = {}) => {
  try {
    const { data, error } = await supabase?.rpc('search_bail_schedule', {
      search_term: searchTerm,
      category_filter: category,
      min_bail: minBail,
      max_bail: maxBail
    });

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || [],
      count: data?.length || 0
    };
  } catch (error) {
    console.error('Error searching bail schedule:', error);
    return {
      success: false,
      error: error?.message || 'Failed to search bail schedule',
      data: [],
      count: 0
    };
  }
};

/**
 * Get all bail schedule entries
 * @returns {Promise<Object>} All bail schedule entries
 */
export const getAllBailSchedule = async () => {
  try {
    const { data, error } = await supabase
      ?.from('bail_schedule')
      ?.select('*')
      ?.order('bail_amount', { ascending: false });

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || [],
      count: data?.length || 0
    };
  } catch (error) {
    console.error('Error fetching bail schedule:', error);
    return {
      success: false,
      error: error?.message || 'Failed to fetch bail schedule',
      data: [],
      count: 0
    };
  }
};

/**
 * Get bail schedule by category
 * @param {string} category - Category to filter by
 * @returns {Promise<Object>} Filtered bail schedule entries
 */
export const getBailScheduleByCategory = async (category) => {
  try {
    const { data, error } = await supabase
      ?.from('bail_schedule')
      ?.select('*')
      ?.eq('category', category)
      ?.order('bail_amount', { ascending: false });

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || [],
      count: data?.length || 0
    };
  } catch (error) {
    console.error('Error fetching bail schedule by category:', error);
    return {
      success: false,
      error: error?.message || 'Failed to fetch bail schedule by category',
      data: [],
      count: 0
    };
  }
};

/**
 * Get bail schedule statistics
 * @returns {Promise<Object>} Statistics by category
 */
export const getBailScheduleStats = async () => {
  try {
    const { data, error } = await supabase?.rpc('get_bail_schedule_stats');

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Error fetching bail schedule stats:', error);
    return {
      success: false,
      error: error?.message || 'Failed to fetch bail schedule statistics',
      data: []
    };
  }
};

/**
 * Get bail amount for specific code section
 * @param {string} codeSection - Code section to lookup
 * @returns {Promise<Object>} Bail amount and details
 */
export const getBailAmountByCode = async (codeSection) => {
  try {
    const { data, error } = await supabase
      ?.from('bail_schedule')
      ?.select('*')
      ?.ilike('code_section', `%${codeSection}%`)
      ?.limit(1)
      ?.single();

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || null
    };
  } catch (error) {
    console.error('Error fetching bail amount by code:', error);
    return {
      success: false,
      error: error?.message || 'Failed to fetch bail amount',
      data: null
    };
  }
};

/**
 * Category display names mapping
 */
export const CATEGORY_NAMES = {
  health_and_safety: 'Health & Safety Code',
  penal_code_felonies: 'Penal Code Felonies',
  vehicle_code: 'Vehicle Code',
  welfare_and_institution: 'Welfare & Institution Code',
  felony_enhancements: 'Felony Enhancements',
  violent_felonies: 'Violent Felonies',
  serious_felonies: 'Serious Felonies',
  penal_code_misdemeanors: 'Penal Code Misdemeanors',
  revenue_and_taxation: 'Revenue & Taxation Code',
  vehicle_code_exceptions: 'Vehicle Code Exceptions',
  misdemeanor_enhancements: 'Misdemeanor Enhancements'
};

/**
 * Get formatted category name
 * @param {string} category - Category key
 * @returns {string} Formatted category name
 */
export const getCategoryName = (category) => {
  return CATEGORY_NAMES?.[category] || category;
};